import * as React from "react";
import { fetchBookingPublic } from "../components/booking-widget/booking-api";
import type { Data } from "../forms/form-data";
import { getWindowJobsiteConfig, type JobsiteRuntimeConfig } from "../types/runtime-config";
import { getModelContext, registerTools } from "./model-context";
import { buildJobsiteWebMcpTools, type JobsiteForms } from "./tools";

const FORM_TIMEOUT_MS = 3000;

/** The forms this site renders, so tools describe the fields this business
 *  actually asks for. Best-effort: a failure costs the shape, not the toolset. */
async function resolveJobsiteForms(config: JobsiteRuntimeConfig): Promise<JobsiteForms> {
  const { apiUrl, siteId } = config;
  const slug = config.business?.slug;

  const contact = apiUrl && siteId
    ? fetch(`${apiUrl}/api/sites/${siteId}/forms/contact`)
        .then((res) => (res.ok ? (res.json() as Promise<{ data?: Data | null }>) : null))
        .then((body) => body?.data ?? null)
        .catch(() => null)
    : Promise.resolve(null);

  const booking = apiUrl && slug
    ? fetchBookingPublic(apiUrl, slug)
        .then((res) => (res.ok ? res.data.formData : null))
        .catch(() => null)
    : Promise.resolve(null);

  const [contactForm, bookingForm] = await Promise.all([contact, booking]);
  return { contact: contactForm, booking: bookingForm };
}

/** Register this jobsite's WebMCP tools for the app's lifetime. Post-mount,
 *  because the tools read `window.__SERVICEPRO_CONFIG__`. */
export function useJobsiteWebMcp(): void {
  React.useEffect(() => {
    const config = getWindowJobsiteConfig();
    // Offering a site's live booking tools inside its owner's editor is wrong.
    if (config.isEditing) return;

    // So is offering them from any frame: the dashboard embeds the live site as
    // a preview thumbnail, and an agent's booking belongs to a visitor's own
    // session. A framed document still gets `modelContext` — `tools` is a
    // policy-controlled feature allowlisted to `self`, so the calls are refused,
    // not absent — which makes this a correctness guard, not an optimization.
    if (typeof window !== "undefined" && window.top !== window.self) return;

    // Before any fetch: with nothing to register on, a browser without WebMCP
    // — today, almost every browser — pays no request for this.
    if (!getModelContext()) return;

    let unregister: (() => void) | undefined;
    let cancelled = false;
    // A stalled API costs the tools their form-specific shape, never the tools:
    // the read-only ones need no document at all.
    void Promise.race([
      resolveJobsiteForms(config),
      new Promise<JobsiteForms>((resolve) => setTimeout(() => resolve({}), FORM_TIMEOUT_MS)),
    ]).then((forms) => {
      if (cancelled) return;
      unregister = registerTools(buildJobsiteWebMcpTools(forms));
    });

    return () => {
      cancelled = true;
      unregister?.();
    };
  }, []);
}
